import React, { useState, useEffect } from 'react';
import { getUserVouchers, createVoucher, updateVoucher } from '../services/firestoreService';
import {useAuth} from "../context/AuthContext";

const VoucherManagement = () => {
    const [vouchers, setVouchers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showPurchaseModal, setShowPurchaseModal] = useState(false);
    const [purchaseAmount, setPurchaseAmount] = useState(50);
    const [purchasing, setPurchasing] = useState(false);
    const [filter, setFilter] = useState('all'); // 'all', 'active', 'used', 'expired'
    const { user } = useAuth();

    useEffect(() => {
        loadVouchers();
    }, [user]);

    const loadVouchers = async () => {
        try {
            setLoading(true);
            setError('');
            const vouchersData = await getUserVouchers(user.uid);
            setVouchers(vouchersData);
        } catch (error) {
            console.error('Error loading vouchers:', error);
            setError('Failed to load your vouchers. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const generateVoucherCode = () => {
        const timestamp = new Date().getTime().toString().slice(-6);
        const randomChars = Math.random().toString(36).substring(2, 8).toUpperCase();
        return `VCH-${timestamp}-${randomChars}`;
    };

    const purchaseVoucher = async () => {
        if (purchaseAmount > user.availableBalance) {
            alert('Insufficient balance to purchase this voucher.');
            return;
        }

        setPurchasing(true);
        try {
            const voucherData = {
                userId: user.uid,
                userName: user.name,
                code: generateVoucherCode(),
                amount: purchaseAmount,
                purchaseDate: new Date().toISOString(),
                expiryDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // 1 year from now
                status: 'active',
                usedDate: null,
                usedFor: null,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            await createVoucher(voucherData);

            // Update user's balance (this would typically be handled by a cloud function)
            // For now, we'll just show a success message

            alert(`Voucher purchased successfully!\nCode: ${voucherData.code}\nAmount: $${purchaseAmount}`);
            setShowPurchaseModal(false);
            setPurchaseAmount(50);
            loadVouchers(); // Reload to show the new voucher
        } catch (error) {
            console.error('Error purchasing voucher:', error);
            alert('Failed to purchase voucher. Please try again.');
        } finally {
            setPurchasing(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            alert('Voucher code copied to clipboard!');
        }).catch(() => {
            alert('Failed to copy voucher code.');
        });
    };

    const formatDate = (dateString) => {
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return 'Invalid Date';
        }
    };

    const getStatusBadge = (voucher) => {
        const now = new Date();
        const expiryDate = new Date(voucher.expiryDate);

        if (voucher.status === 'used') {
            return { label: 'Used', color: 'blue', icon: '✅' };
        }

        if (now > expiryDate) {
            return { label: 'Expired', color: 'red', icon: '❌' };
        }

        return { label: 'Active', color: 'green', icon: '🟢' };
    };

    const isVoucherExpired = (voucher) => {
        const now = new Date();
        const expiryDate = new Date(voucher.expiryDate);
        return now > expiryDate;
    };

    const filteredVouchers = vouchers.filter(voucher => {
        if (filter === 'all') return true;
        if (filter === 'active') return voucher.status === 'active' && !isVoucherExpired(voucher);
        if (filter === 'used') return voucher.status === 'used';
        if (filter === 'expired') return isVoucherExpired(voucher);
        return true;
    });

    const stats = {
        total: vouchers.length,
        active: vouchers.filter(v => v.status === 'active' && !isVoucherExpired(v)).length,
        used: vouchers.filter(v => v.status === 'used').length,
        expired: vouchers.filter(v => isVoucherExpired(v)).length,
        totalValue: vouchers.reduce((sum, voucher) => sum + voucher.amount, 0),
        availableValue: vouchers
            .filter(v => v.status === 'active' && !isVoucherExpired(v))
            .reduce((sum, voucher) => sum + voucher.amount, 0)
    };

    const presetAmounts = [25, 50, 100, 250, 500];

    if (loading) {
        return (
            <div className="space-y-6">
                {/* Loading Skeleton for Purchase Card */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="animate-pulse">
                        <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                </div>

                {/* Loading Skeleton for Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="bg-white rounded-lg shadow-sm p-6">
                            <div className="animate-pulse">
                                <div className="h-8 bg-gray-200 rounded w-1/2 mx-auto mb-2"></div>
                                <div className="h-4 bg-gray-200 rounded w-2/3 mx-auto"></div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Loading Skeleton for Vouchers */}
                <div className="bg-white rounded-lg shadow-sm">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                    </div>
                    <div className="p-6">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="animate-pulse border border-gray-200 rounded-lg p-6 mb-4">
                                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                                <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="text-center py-8">
                        <div className="text-red-500 text-4xl mb-4">⚠️</div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Vouchers</h3>
                        <p className="text-gray-600 mb-4">{error}</p>
                        <button
                            onClick={loadVouchers}
                            className="bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Purchase Voucher Card */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Purchase Vouchers</h3>
                        <p className="text-gray-600">Buy vouchers to use for contributions</p>
                    </div>
                    <div className="flex items-center space-x-4">
                        <div className="text-right">
                            <p className="text-sm text-gray-600">Available Balance</p>
                            <p className="text-2xl font-bold text-green-600">${user.availableBalance || 0}</p>
                        </div>
                        <button
                            onClick={() => setShowPurchaseModal(true)}
                            className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors font-medium"
                        >
                            Purchase Voucher
                        </button>
                    </div>
                </div>
            </div>

            {/* Voucher Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg shadow-sm p-6 text-center">
                    <div className="text-2xl font-bold text-primary-600">{stats.total}</div>
                    <div className="text-sm text-gray-600">Total Vouchers</div>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-6 text-center">
                    <div className="text-2xl font-bold text-green-600">{stats.active}</div>
                    <div className="text-sm text-gray-600">Active</div>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-6 text-center">
                    <div className="text-2xl font-bold text-blue-600">{stats.used}</div>
                    <div className="text-sm text-gray-600">Used</div>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-6 text-center">
                    <div className="text-2xl font-bold text-red-600">{stats.expired}</div>
                    <div className="text-sm text-gray-600">Expired</div>
                </div>
            </div>

            {/* Value Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-sm text-gray-600">Total Voucher Value</p>
                            <p className="text-2xl font-bold text-purple-600">${stats.totalValue}</p>
                        </div>
                        <div className="text-2xl">💰</div>
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-sm text-gray-600">Available Balance</p>
                            <p className="text-2xl font-bold text-green-600">${stats.availableValue}</p>
                        </div>
                        <div className="text-2xl">💳</div>
                    </div>
                </div>
            </div>

            {/* Vouchers List */}
            <div className="bg-white rounded-lg shadow-sm">
                <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-900">My Vouchers</h3>

                        {/* Filter Buttons */}
                        <div className="flex space-x-2">
                            <button
                                onClick={() => setFilter('all')}
                                className={`px-3 py-1 text-sm font-medium rounded-lg transition-colors ${
                                    filter === 'all'
                                        ? 'bg-primary-500 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                All ({stats.total})
                            </button>
                            <button
                                onClick={() => setFilter('active')}
                                className={`px-3 py-1 text-sm font-medium rounded-lg transition-colors ${
                                    filter === 'active'
                                        ? 'bg-green-500 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                Active ({stats.active})
                            </button>
                            <button
                                onClick={() => setFilter('used')}
                                className={`px-3 py-1 text-sm font-medium rounded-lg transition-colors ${
                                    filter === 'used'
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                Used ({stats.used})
                            </button>
                            <button
                                onClick={() => setFilter('expired')}
                                className={`px-3 py-1 text-sm font-medium rounded-lg transition-colors ${
                                    filter === 'expired'
                                        ? 'bg-red-500 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                Expired ({stats.expired})
                            </button>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    {filteredVouchers.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="text-gray-400 text-6xl mb-4">
                                {filter === 'all' ? '🎫' :
                                    filter === 'active' ? '🟢' :
                                        filter === 'used' ? '✅' : '❌'}
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                {filter === 'all'
                                    ? 'No Vouchers Yet'
                                    : filter === 'active'
                                        ? 'No Active Vouchers'
                                        : filter === 'used'
                                            ? 'No Used Vouchers'
                                            : 'No Expired Vouchers'
                                }
                            </h3>
                            <p className="text-gray-600">
                                {filter === 'all'
                                    ? "You haven't purchased any vouchers yet. Purchase your first voucher to start contributing."
                                    : filter === 'active'
                                        ? "You don't have any active vouchers. Purchase a new voucher to get started."
                                        : filter === 'used'
                                            ? "You haven't used any vouchers yet."
                                            : "You don't have any expired vouchers."
                                }
                            </p>
                            {filter !== 'all' && (
                                <button
                                    onClick={() => setFilter('all')}
                                    className="mt-4 bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors"
                                >
                                    View All Vouchers
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {filteredVouchers.map((voucher) => {
                                const status = getStatusBadge(voucher);
                                return (
                                    <div key={voucher.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <div className="flex items-center space-x-3 mb-2">
                                                    <h4 className="font-mono font-semibold text-gray-900 text-lg">
                                                        {voucher.code}
                                                    </h4>
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${status.color}-100 text-${status.color}-800`}>
                            <span className="mr-1">{status.icon}</span>
                                                        {status.label}
                          </span>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-gray-600">
                                                    <div>
                                                        <span className="font-medium">Amount:</span>
                                                        <span className="ml-2 text-green-600 font-semibold">${voucher.amount}</span>
                                                    </div>
                                                    <div>
                                                        <span className="font-medium">Purchased:</span>
                                                        <span className="ml-2">{formatDate(voucher.purchaseDate)}</span>
                                                    </div>
                                                    <div>
                                                        <span className="font-medium">Expires:</span>
                                                        <span className="ml-2">{formatDate(voucher.expiryDate)}</span>
                                                    </div>
                                                    {voucher.usedDate && (
                                                        <div>
                                                            <span className="font-medium">Used:</span>
                                                            <span className="ml-2">{formatDate(voucher.usedDate)}</span>
                                                        </div>
                                                    )}
                                                </div>

                                                {voucher.usedFor && (
                                                    <p className="text-sm text-gray-600 mt-2">
                                                        Used for: {voucher.usedFor}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="flex flex-col space-y-2 ml-4">
                                                {voucher.status === 'active' && !isVoucherExpired(voucher) && (
                                                    <>
                                                        <button
                                                            onClick={() => copyToClipboard(voucher.code)}
                                                            className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium whitespace-nowrap"
                                                        >
                                                            Copy Code
                                                        </button>
                                                        <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium whitespace-nowrap">
                                                            View Details
                                                        </button>
                                                    </>
                                                )}
                                                {(voucher.status === 'used' || isVoucherExpired(voucher)) && (
                                                    <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium whitespace-nowrap">
                                                        View Details
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Refresh Button */}
                    {filteredVouchers.length > 0 && (
                        <div className="mt-6 flex justify-between items-center">
                            <button
                                onClick={loadVouchers}
                                className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
                            >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                <span>Refresh</span>
                            </button>

                            <div className="text-sm text-gray-500">
                                Showing {filteredVouchers.length} of {vouchers.length} vouchers
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Purchase Modal */}
            {showPurchaseModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Purchase Voucher</h3>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Voucher Amount
                                </label>
                                <div className="grid grid-cols-3 gap-2">
                                    {presetAmounts.map((amount) => (
                                        <button
                                            key={amount}
                                            onClick={() => setPurchaseAmount(amount)}
                                            className={`py-3 border rounded-lg text-sm font-medium transition-colors ${
                                                purchaseAmount === amount
                                                    ? 'border-primary-500 bg-primary-500 text-white'
                                                    : 'border-gray-300 text-gray-700 hover:border-primary-300'
                                            }`}
                                        >
                                            ${amount}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Custom Amount
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                                    <input
                                        type="number"
                                        value={purchaseAmount}
                                        onChange={(e) => setPurchaseAmount(Number(e.target.value))}
                                        className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                        min="10"
                                        max="1000"
                                    />
                                </div>
                            </div>

                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <h5 className="font-medium text-blue-900 mb-2">Voucher Information</h5>
                                <ul className="text-sm text-blue-800 space-y-1">
                                    <li>• Voucher will be generated immediately after purchase</li>
                                    <li>• Valid for 12 months from purchase date</li>
                                    <li>• Can be used for any active support case</li>
                                    <li>• Non-refundable and non-transferable</li>
                                </ul>
                            </div>

                            {/* Purchase Summary */}
                            <div className="bg-gray-50 rounded-lg p-4">
                                <h5 className="font-medium text-gray-900 mb-2">Purchase Summary</h5>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span>Voucher Amount:</span>
                                        <span className="font-semibold">${purchaseAmount}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Your Balance:</span>
                                        <span className="font-semibold">${user.availableBalance || 0}</span>
                                    </div>
                                    <div className="flex justify-between border-t pt-2">
                                        <span>Remaining Balance:</span>
                                        <span className={`font-semibold ${
                                            purchaseAmount > user.availableBalance ? 'text-red-600' : 'text-green-600'
                                        }`}>
                      ${(user.availableBalance || 0) - purchaseAmount}
                    </span>
                                    </div>
                                </div>
                            </div>

                            {purchaseAmount > user.availableBalance && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                    <p className="text-red-700 text-sm text-center">
                                        ❌ Insufficient balance. Please reduce the voucher amount.
                                    </p>
                                </div>
                            )}

                            <div className="flex space-x-3 pt-4">
                                <button
                                    onClick={() => {
                                        setShowPurchaseModal(false);
                                        setPurchaseAmount(50);
                                    }}
                                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={purchaseVoucher}
                                    disabled={purchasing || purchaseAmount > user.availableBalance || purchaseAmount < 10}
                                    className="flex-1 bg-primary-500 text-white px-4 py-3 rounded-lg hover:bg-primary-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium flex items-center justify-center"
                                >
                                    {purchasing ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Processing...
                                        </>
                                    ) : (
                                        `Purchase for $${purchaseAmount}`
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VoucherManagement;